import { useContext, useState } from 'react';
import { QueryFunctionContext, useQuery, useQueryClient } from 'react-query';
import { databaseService } from './database.service';
import { InstanceFormDefaultValues, Instances } from '@chiroup/core';
import { MeContext } from '../../../contexts/me.context';

const getDatabaseItem = <T>(skipInitialFetch?: boolean) => {
  return async (context: QueryFunctionContext) => {
    if (skipInitialFetch) {
      return;
    }
    const clinicId = context.queryKey[1] as number;
    const instanceKey = context.queryKey[2] as string;
    const id = context.queryKey[3];

    if (id === 'add') {
      return InstanceFormDefaultValues[instanceKey as Instances] as T;
    }

    return databaseService.get<T>({
      clinicId,
      database: instanceKey,
      id: id as number,
    });
  };
};

const useDatabaseItem = <T>({
  instanceKey,
  id,
  skipInitialFetch = false,
}: {
  instanceKey: string;
  id: number | 'add';
  skipInitialFetch?: boolean;
}) => {
  const { me } = useContext(MeContext);
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const { data, isFetching } = useQuery({
    queryKey: ['databaseItem', me.selectedClinic?.ID, instanceKey, id],
    queryFn: getDatabaseItem<T>(skipInitialFetch),
    enabled: !!instanceKey && !!id,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    staleTime: 5000,
  });

  const save = async (item: T) => {
    setIsSaving(true);
    const clinicId = me.selectedClinic?.ID;
    if (!clinicId) {
      return;
    }
    const res = await databaseService.save<T>({
      clinicId,
      database: instanceKey,
      item,
    });
    queryClient.invalidateQueries([
      'databaseItem',
      me.selectedClinic?.ID,
      instanceKey,
      id,
    ]);
    queryClient.invalidateQueries(['database']);
    setIsSaving(false);
    return res;
  };

  const del = async (idToDelete: number) => {
    // Just in case we somehow get in a bad state, let's not trust the id in the hook!
    const clinicId = me.selectedClinic?.ID;
    if (!clinicId) {
      return;
    }
    const res = await databaseService.del({
      clinicId,
      database: instanceKey,
      id: idToDelete,
    });
    queryClient.invalidateQueries([
      'databaseItem',
      me.selectedClinic?.ID,
      instanceKey,
      idToDelete,
    ]);
    queryClient.invalidateQueries(['database']);
    return res;
  };

  return {
    data,
    isFetching,
    isSaving,
    save,
    del,
  };
};

export default useDatabaseItem;
